import ReactCardFlip from "react-card-flip";
import { useSelector } from "react-redux";
import { createArray } from "../utils/functions";
import { FaFacebookF, FaTwitter, FaInstagram } from "react-icons/fa";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { IoCallOutline } from "react-icons/io5";
import { TbWorld } from "react-icons/tb";
import { Modal } from "antd";
import { getBusinesses } from "../actions/acc_actions";
import { useQuery } from "@tanstack/react-query";
import ImageFilePreview from "./ImageFilePreview";
import Loading from "../utils/Loading";
import { useParams } from "react-router-dom";

const FlipCardLocal = ({
  flipped = false,
  className = "",
  card,
  welcomeRewardAssests = {},
  flipCard,
  logo,
  background,
  layout,
  defaultAssets,
  rewardsAssets = [],
  showEncourageMsg,
}) => {
  const { accUrn } = useParams();

  const { isLoading: businessLoading, data: businessesData } = useQuery({
    queryKey: ["userBusinesses", accUrn],
    queryFn: () => getBusinesses(accUrn),
    enabled: accUrn ? true : false,
  });

  if (businessLoading) return <Loading />;
  const business = businessesData?.data.at(-1);

  if (!business) return;

  const businessContact = business?.config?.contact;
  const encourageMsg = card.encourageMessage;

  let cardLayout;
  if (layout === "layout1") {
    cardLayout = { columns: 2, rows: 3 };
  } else if (layout === "layout2") {
    cardLayout = { columns: 3, rows: 3 };
  }

  const totalSlots = cardLayout.columns * cardLayout.rows;
  const hasWelcomeReward = !!welcomeRewardAssests?.targetIcon?.url;

  // Initialize empty reward array with defaultAssets
  const rewards = Array.from({ length: totalSlots }, (_, i) => ({
    key: `reward-${i}`,
    img: defaultAssets?.targetIcon?.url,
  }));

  // Insert welcome reward at the first slot if exists
  if (hasWelcomeReward) {
    rewards[0] = {
      key: `reward-0`,
      img: welcomeRewardAssests.targetIcon.url,
    };
  }

  // Insert each rewardsAsset based on qualifyingPurchases (shift +1 if welcome exists)
  rewardsAssets?.forEach((reward, index) => {
    const baseIndex = reward.qualifyingPurchases - 1; // 1-based to 0-based
    const targetIndex = hasWelcomeReward ? baseIndex + 1 : baseIndex + 1;

    if (targetIndex < totalSlots) {
      rewards[targetIndex] = {
        key: `reward-${targetIndex}`,
        img: reward.targetIcon?.url || defaultAssets?.targetIcon?.url,
      };
    }
  });

  const handleCardClick = () => {
    flipCard();
  };

  const info = (termsText) => {
    Modal.info({
      title: "Terms & Conditions",
      content: (
        <div>
          <p>{termsText}</p>
        </div>
      ),
      onOk() {},
      okButtonProps: {
        style: {
          backgroundColor: "#F11E69", // Change background color
          borderRadius: "5px", // Rounded corners
        },
      },
    });
  };

  return (
    <div className={`card-container ${className}`} onClick={handleCardClick}>
      <ReactCardFlip isFlipped={flipped} flipDirection="horizontal">
        <div
          className="flip-card-front"
          style={{
            backgroundColor: card.colors.bgFront,
            backgroundImage: `url(${background?.localUrl})`,
          }}
        >
          <div className="flip-card-front-header">
            {!!logo ? (
              <img className="logo" src={logo?.localUrl} alt="" />
            ) : (
              <div className="logo-gray">Logo</div>
            )}
            {showEncourageMsg && (
              <h6
                style={{
                  textAlign: "center",
                  color: card.colors.textFront,
                }}
              >
                {encourageMsg}
              </h6>
            )}
          </div>
          <div
            className="rewards"
            style={{
              width: "100%",
              height: "100%",
              display: "grid",
              gridTemplateColumns: `repeat(${cardLayout.columns}, 1fr)`,
              gridTemplateRows: `repeat(${cardLayout.rows}, 1fr)`,
            }}
          >
            {/* {rewards} */}
            {rewards.map(({ key, img }) => (
              <ImageFilePreview className="reward-img" key={key} img={img} />
            ))}
          </div>
        </div>

        <div
          style={{
            backgroundColor: card.colors.bgBack,
            color: card.colors.textBack,
          }}
          className="flip-card-back"
        >
          <div>
            <h5>{business?.name}</h5>
            {business?.config?.locations_hint && (
              <h6>{business?.config?.locations_hint}</h6>
            )}
          </div>

          {card.extraInfo && <h6 className="extra_info">{card.extraInfo}</h6>}
          <ul>
            {businessContact?.facebook_tag && (
              <li>
                <a
                  href={`https://facebook.com/${businessContact?.facebook_tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebookF
                    className="social-icon"
                    style={{
                      color: card.colors.bgBack,
                      backgroundColor: card.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.facebook_tag}</h6>
                </a>
              </li>
            )}
            {businessContact?.twitter_tag && (
              <li>
                <a
                  href={`https://twitter.com/${businessContact?.twitter_tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaTwitter
                    className="social-icon"
                    style={{
                      color: card.colors.bgBack,
                      backgroundColor: card.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.twitter_tag}</h6>
                </a>
              </li>
            )}
            {businessContact?.instagram_tag && (
              <li>
                <a
                  href={`https://instagram.com/${businessContact?.instagram_tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram
                    className="social-icon"
                    style={{
                      color: card.colors.bgBack,
                      backgroundColor: card.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.instagram_tag}</h6>
                </a>
              </li>
            )}
            {businessContact?.email && (
              <li>
                <a href={`mailto:${businessContact?.email}`}>
                  <MdOutlineAlternateEmail
                    className="social-icon"
                    style={{
                      color: card.colors.bgBack,
                      backgroundColor: card.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.email}</h6>
                </a>
              </li>
            )}
            {businessContact?.phone && (
              <li>
                <a href={`tel:${businessContact?.phone}`}>
                  <IoCallOutline
                    className="social-icon"
                    style={{
                      color: card.colors.bgBack,
                      backgroundColor: card.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.phone}</h6>
                </a>
              </li>
            )}
            {businessContact?.website && (
              <li>
                <a
                  href={businessContact?.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TbWorld
                    className="social-icon"
                    style={{
                      color: card.colors.bgBack,
                      backgroundColor: card.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.website}</h6>
                </a>
              </li>
            )}
          </ul>
          {card.terms && (
            <a href="#terms" onClick={() => info(card.terms)}>
              Terms
            </a>
          )}
        </div>
      </ReactCardFlip>
    </div>
  );
};

export default FlipCardLocal;
